/* eslint-disable no-param-reassign */
import { createAction, createSlice, createSelector } from '@reduxjs/toolkit';

import createRequestActionTypes from 'store/helpers/createRequestActionTypes';
import { handleClearErrors, onFailure } from 'store/helpers/commonHandlers';

const name = 'accounts';

const initialState = {
  user: null,
  errors: {},
};

/** ActionType for Request */
export const [
  EMAIL_LOGIN,
  EMAIL_LOGIN_SUCCESS,
  EMAIL_LOGIN_FAILURE,
] = createRequestActionTypes(name, 'EMAIL_LOGIN');
export const [
  EMAIL_SIGNUP,
  EMAIL_SIGNUP_SUCCESS,
  EMAIL_SIGNUP_FAILURE,
] = createRequestActionTypes(name, 'EMAIL_SIGNUP');
export const [
  REQUEST_RESET_PASSWORD,
  REQUEST_RESET_PASSWORD_SUCCESS,
  REQUEST_RESET_PASSWORD_FAILURE,
] = createRequestActionTypes(name, 'REQUEST_RESET_PASSWORD');
export const [
  RESET_PASSWORD,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
] = createRequestActionTypes(name, 'RESET_PASSWORD');
export const [
  VALIDATE_RESET_FORM,
  VALIDATE_RESET_FORM_SUCCESS,
  VALIDATE_RESET_FORM_FAILURE,
] = createRequestActionTypes(name, 'VALIDATE_RESET_FORM');
export const [
  UPDATE_PASSWORD,
  UPDATE_PASSWORD_SUCCESS,
  UPDATE_PASSWORD_FAILURE,
] = createRequestActionTypes(name, 'UPDATE_PASSWORD');

/** Common handler */
const onSuccessAuth = (state, { payload: user }) => {
  state.user = user;
  state.errors = {};
};
const onFailureAuth = (key) => (state, { payload: { response = {} } }) => ({
  ...initialState,
  errors: { ...state.errors, [key]: response },
});

const slice = createSlice({
  name,
  initialState,
  reducers: {
    initAuth: () => initialState,
    logout: () => initialState,
    clearErrors: handleClearErrors,
  },
  extraReducers: {
    [EMAIL_LOGIN_SUCCESS]: onSuccessAuth,
    [EMAIL_LOGIN_FAILURE]: onFailureAuth('auth'),
    [EMAIL_SIGNUP_SUCCESS]: onSuccessAuth,
    [EMAIL_SIGNUP_FAILURE]: onFailureAuth('auth'),
    [REQUEST_RESET_PASSWORD_FAILURE]: onFailureAuth('auth'),
    [RESET_PASSWORD_FAILURE]: onFailureAuth('auth'),
    [VALIDATE_RESET_FORM_FAILURE]: onFailure('validateResetForm'),
    [UPDATE_PASSWORD_FAILURE]: onFailure('updatePassword'),
  },
});

/** Action for Request */
export const emailLogin = createAction(EMAIL_LOGIN);
export const emailSignup = createAction(EMAIL_SIGNUP);
export const requestResetPassword = createAction(REQUEST_RESET_PASSWORD);
export const resetPassword = createAction(RESET_PASSWORD);
export const validateResetForm = createAction(VALIDATE_RESET_FORM);
export const updatePassword = createAction(UPDATE_PASSWORD);

/** General Action */
export const { initAuth, logout, clearErrors } = slice.actions;

/** Reducer */
export const accounts = slice.reducer;

/** Selectors */
const errorSelector = createSelector(
  (state) => state.errors,
  (errors) => ({ errors }),
);
const userSelector = createSelector(
  (state) => state.user,
  (user) => ({
    user,
    isAuthenticated: !!user,
  }),
);

export const selectors = {
  errors: (state) => errorSelector(state[name]),
  user: (state) => userSelector(state[name]),
};

export default slice;
